// *** This file is part of DbGate Premium ***
import moment from 'moment';
import _ from 'lodash';
import formatFileSize from '../utility/formatFileSize';
const isCypress = Boolean(window['Cypress']);
export class ChartDataCreator {
    constructor(chart, themeDefinition, isExport) {
        var _a;
        this.chart = chart;
        this.themeDefinition = themeDefinition;
        this.isExport = isExport;
        this.timeAxis = null;
        this.plugins = [];
        this.labelValues = this.chart.bucketKeysOrdered;
        this.chartType = this.chart.definition.chartType;
        if (!this.chart)
            return null;
        if (!this.chart.bucketKeysOrdered)
            return null;
        if (this.chart.bucketKeysOrdered.length == 0)
            return null;
        this.detectTimeAxis();
        this.detectPlugins();
        this.data = {
            labels: this.timeAxis && this.chartType === 'timeline' ? this.timeAxis.map(x => x.toDate()) : this.labelValues,
            datasets: ((_a = this.chart.groups) === null || _a === void 0 ? void 0 : _a.length) > 0
                ? [...this.chart.groups].flatMap(group => this.chart.definition.ydefs.map((ydef, columnIndex) => {
                    var _a;
                    return {
                        label: this.chart.definition.ydefs.length == 1
                            ? group
                            : `${group} - ${(_a = ydef.title) !== null && _a !== void 0 ? _a : _.startCase(ydef.field)}`,
                        data: this.chart.bucketKeysOrdered.map(key => { var _a; return (_a = this.chart.buckets[`${group}::${key}`]) === null || _a === void 0 ? void 0 : _a[ydef.field]; }),
                        borderWidth: 1,
                    };
                }))
                : this.chart.definition.ydefs.map((ydef, columnIndex) => {
                    var _a;
                    return {
                        label: (_a = ydef.title) !== null && _a !== void 0 ? _a : _.startCase(ydef.field),
                        data: this.chart.bucketKeysOrdered.map(key => { var _a; return (_a = this.chart.buckets[key]) === null || _a === void 0 ? void 0 : _a[ydef.field]; }),
                        borderWidth: 1,
                    };
                }),
        };
        this.options = this.getOptions();
        this.options.plugins = this.getPluginOptions();
        // console.log('RES', res);
        // console.log('OPTIONS', options);
    }
    detectTimeAxis() {
        if (!this.chart.definition.xdef.transformFunction.startsWith('date:')) {
            return;
        }
        if (this.chart.definition.chartType != 'timeline') {
            return;
        }
        const res = [];
        for (const label of this.labelValues) {
            const parsed = moment(label);
            if (!parsed.isValid())
                return null;
            const iso = parsed.toISOString();
            if (iso < '1850-01-01T00:00:00' || iso > '2150-01-01T00:00:00')
                return null;
            res.push(parsed);
        }
        this.timeAxis = res;
    }
    detectPlugins() {
        if (this.chart.definition.useDataLabels) {
            this.plugins.push('dataLabels');
        }
        if (this.chart.definition.useDataLabels != false &&
            this.chart.definition.chartType == 'pie' &&
            this.chart.definition.ydefs.length == 1 &&
            // outlabels plugin does not work in exported HTML
            !this.isExport) {
            this.plugins.push('outlabels');
        }
        if (this.chart.definition.chartType == 'timeline' ||
            this.chart.definition.chartType == 'line' ||
            this.chart.definition.chartType == 'bar') {
            this.plugins.push('zoom');
        }
    }
    getLabels(labelValues, timeAxis, chartType) {
        if (!timeAxis)
            return labelValues;
        if (chartType === 'timeline')
            return timeAxis.map(x => x.toDate());
        return labelValues;
        // return timeAxis.map(x => x.format('D. M. YYYY'));
    }
    getOptions() {
        const res = {
            scales: {},
            responsive: !isCypress,
            // maintainAspectRatio: false,
            animation: !isCypress,
            plugins: null,
            maintainAspectRatio: this.chartType === 'timeline' || this.chartType === 'line' || this.chartType === 'bar',
        };
        if (this.timeAxis && this.chartType === 'timeline') {
            res.scales = {
                x: {
                    type: 'time',
                    distribution: 'linear',
                    time: {
                        tooltipFormat: 'D. M. YYYY HH:mm',
                        displayFormats: {
                            millisecond: 'HH:mm:ss.SSS',
                            second: 'HH:mm:ss',
                            minute: 'HH:mm',
                            hour: 'D.M hA',
                            day: 'D. M.',
                            week: 'D. M. YYYY',
                            month: 'MM-YYYY',
                            quarter: '[Q]Q - YYYY',
                            year: 'YYYY',
                        },
                    },
                },
            };
        }
        if (this.plugins.includes('outlabels')) {
            res['layout'] = {
                padding: { left: 200, right: 200, top: 10, bottom: 10 },
            };
        }
        return res;
    }
    getPluginOptions() {
        var _a, _b, _c;
        const xtitle = (_a = this.chart.definition.xdef.title) !== null && _a !== void 0 ? _a : _.startCase(this.chart.definition.xdef.field);
        const chartTitle = (_b = this.chart.definition.title) !== null && _b !== void 0 ? _b : (this.chart.definition.ydefs.length == 1
            ? `${_.startCase(this.chart.definition.ydefs[0].title)} by ${xtitle}`
            : `By ${xtitle}`);
        const dataLabelFormatter = (_c = this.chart.definition.dataLabelFormatter) !== null && _c !== void 0 ? _c : 'number';
        const res = {};
        if (chartTitle) {
            res['title'] = {
                display: true,
                text: chartTitle,
            };
            if (this.plugins.includes('outlabels')) {
                res['title']['padding'] = { top: 0, bottom: 24 };
            }
        }
        res['zoom'] = {
            zoom: {
                wheel: {
                    enabled: true,
                },
                pinch: {
                    enabled: true,
                },
                drag: {
                    enabled: true,
                    modifierKey: 'shift',
                },
                mode: 'x',
            },
            pan: {
                enabled: true,
                mode: 'x',
            },
        };
        if (!this.isExport) {
            res['datalabels'] = {
                formatter: dataLabelFormatter == 'size:bytes'
                    ? value => formatFileSize(value)
                    : dataLabelFormatter == 'size:kb'
                        ? value => formatFileSize(value * 1000)
                        : dataLabelFormatter == 'size:mb'
                            ? value => formatFileSize(value * 1000 * 1000)
                            : dataLabelFormatter == 'size:gb'
                                ? value => formatFileSize(value * 1000 * 1000 * 1000)
                                : value => (typeof value === 'number' ? value.toLocaleString() : value),
            };
        }
        if (this.plugins.includes('outlabels')) {
            res['legend'] = {
                display: false,
            };
        }
        return res;
    }
}
